import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import React from 'react';
import { Controller as FormController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import sendSupportRequest from '../../services/support';
import ContactInfoBlock from '../contact-info';
import Loader from '../loader';
import Notification from '../notification';

const validationSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  email: yup
    .string()
    .email('Email must be a valid email')
    .required('Email is required'),
  topic: yup.string().required('Topic is required'),
  product: yup.string().required('Product is required'),
  message: yup.string().required('Message is required')
});

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: 640,
    padding: theme.spacing(15, 5),
    margin: '0 auto'
  },
  title: {
    marginBottom: theme.spacing(16),
    textTransform: 'uppercase'
  },
  subTitle: {
    marginBottom: theme.spacing(10),
    fontWeight: 400
  },
  formControl: {
    marginBottom: theme.spacing(2.5)
  },
  formLabel: {
    fontSize: 14
  },
  formLabelError: {
    color: theme.palette.error.main,
    '&+span': {
      color: theme.palette.error.main
    }
  },
  formControlTextarea: {
    marginBottom: theme.spacing(14)
  },
  selectGroup: {
    marginBottom: theme.spacing(8)
  },
  loader: {
    transform: 'translateY(-24px)'
  }
}));

const SupportContent = () => {
  const { t } = useTranslation();
  const classes = useStyles({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [notification, setNotification] = React.useState({
    open: false,
    type: '',
    message: ''
  });

  const { handleSubmit, control, errors, reset } = useForm({
    validationSchema,
    defaultValues: {
      name: '',
      email: '',
      country: '',
      businessName: '',
      phoneNumber: '',
      topic: '',
      product: '',
      message: ''
    }
  });

  const onSubmit = data => {
    setIsLoading(true);

    sendSupportRequest(data)
      .then(result => {
        setNotification(true);

        if (result.success) {
          return setNotification({
            open: true,
            type: 'success',
            message: 'Thank you for your appeal!'
          });
        }
        return setNotification({
          open: true,
          type: 'error',
          message: result.message
        });
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        reset();
        setIsLoading(false);
      });
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setNotification(false);
  };

  return (
    <div className={classes.root}>
      {notification.open && (
        <Notification
          open={notification.open}
          onClose={handleClose}
          type={notification.type}
          message={notification.message}
        />
      )}
      <Typography className={classes.title} variant="h1" align="center">
        {t('supportPage.title')}
      </Typography>

      {/* Form */}
      <div className={classes.formWrap}>
        <form
          className={classes.form}
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          {/* Your name */}
          <FormController
            as={TextField}
            control={control}
            defaultValue=""
            type="text"
            name="name"
            label={t('supportPage.name')}
            fullWidth
            className={classes.formControl}
            disabled={isLoading}
            error={!!errors.name}
            helperText={errors.name ? errors.name.message : ''}
            FormHelperTextProps={{
              classes: {
                root: classes.helper
              }
            }}
          />

          {/* Email */}
          <FormController
            as={TextField}
            control={control}
            defaultValue=""
            type="email"
            name="email"
            label={t('supportPage.email')}
            fullWidth
            className={classes.formControl}
            disabled={isLoading}
            error={!!errors.email}
            helperText={errors.email ? errors.email.message : ''}
            FormHelperTextProps={{
              classes: {
                root: classes.helper
              }
            }}
          />

          {/* Country */}
          <FormController
            as={TextField}
            control={control}
            defaultValue=""
            type="text"
            name="country"
            label={t('supportPage.country')}
            fullWidth
            className={classes.formControl}
            disabled={isLoading}
          />

          {/* Business name  */}
          <FormController
            as={TextField}
            control={control}
            defaultValue=""
            type="text"
            name="businessName"
            label={t('supportPage.businessName')}
            fullWidth
            className={classes.formControl}
            disabled={isLoading}
          />

          {/* Phone number */}
          <FormController
            as={TextField}
            control={control}
            defaultValue=""
            type="text"
            name="phoneNumber"
            label={t('supportPage.phoneNumber')}
            fullWidth
            className={classes.formControl}
            disabled={isLoading}
          />

          {/* Select group */}
          <div className={classes.selectGroup}>
            <Grid container spacing={3}>
              {/* Topic */}
              <Grid item xs={12} md={6}>
                <FormControl required fullWidth>
                  <InputLabel
                    id="topic-select-label"
                    className={classes.formLabel}
                  >
                    {errors.topic ? (
                      <span className={classes.formLabelError}>
                        {errors.topic.message}
                      </span>
                    ) : (
                      t('supportPage.topic.label')
                    )}
                  </InputLabel>
                  <FormController
                    as={
                      <Select
                        labelId="topic-select-label"
                        id="topic-select"
                        disabled={isLoading}
                      >
                        <MenuItem value="newInquiry">
                          {t('supportPage.topic.New_inquiry')}
                        </MenuItem>
                        <MenuItem value="customSolution">
                          {t('supportPage.topic.Custom_solution')}
                        </MenuItem>
                        <MenuItem value="partnership">
                          {t('supportPage.topic.Partnership')}
                        </MenuItem>
                        <MenuItem value="techSupport">
                          {t('supportPage.topic.Tech_support')}
                        </MenuItem>
                        <MenuItem value="other">
                          {t('supportPage.topic.Other')}
                        </MenuItem>
                      </Select>
                    }
                    control={control}
                    name="topic"
                    defaultValue=""
                    error={!!errors.topic}
                  />
                </FormControl>
              </Grid>

              {/* Product */}
              <Grid item xs={12} md={6}>
                <FormControl required fullWidth>
                  <InputLabel
                    id="product-select-label"
                    className={classes.formLabel}
                  >
                    {errors.product ? (
                      <span className={classes.formLabelError}>
                        {errors.product.message}
                      </span>
                    ) : (
                      t('supportPage.product.label')
                    )}
                  </InputLabel>
                  <FormController
                    as={
                      <Select
                        labelId="product-select-label"
                        id="product-select"
                        disabled={isLoading}
                      >
                        <MenuItem value="editor">
                          {t('supportPage.product.Editor')}
                        </MenuItem>
                        <MenuItem value="ios">
                          {t('supportPage.product.IOS')}
                        </MenuItem>
                        <MenuItem value="android">
                          {t('supportPage.product.Android')}
                        </MenuItem>
                        <MenuItem value="whiteLabel">
                          {t('supportPage.product.White_Label')}
                        </MenuItem>
                        <MenuItem value="enterprise">
                          {t('supportPage.product.Enterprise')}
                        </MenuItem>
                        <MenuItem value="enterpriseEditor">
                          {t('supportPage.product.Enterprise_Editor')}
                        </MenuItem>
                        <MenuItem value="dataAnalytics">
                          {t('supportPage.product.Data_Analytics')}
                        </MenuItem>
                        <MenuItem value="other">
                          {t('supportPage.topic.Other')}
                        </MenuItem>
                      </Select>
                    }
                    control={control}
                    name="product"
                    defaultValue=""
                    error={!!errors.product}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </div>

          {/* Message */}
          <FormController
            as={TextField}
            control={control}
            defaultValue=""
            type="text"
            name="message"
            label={t('supportPage.message')}
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            className={classes.formControlTextarea}
            disabled={isLoading}
            error={!!errors.message}
            helperText={errors.message ? errors.message.message : ''}
            FormHelperTextProps={{
              classes: {
                root: classes.helper
              }
            }}
          />

          {/* Loader */}
          {isLoading && (
            <Loader className={classes.loader} message="Sending ..." />
          )}

          {/* Submit button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            {t('supportPage.submitButton')}
          </Button>
        </form>
      </div>

      {/* Address and socila links */}
      <ContactInfoBlock />
    </div>
  );
};

export default SupportContent;
